import {
  AnimatePresence,
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useVelocity,
} from 'framer-motion';
import { useEffect, useState } from 'react';

const CURSOR_SPRING = {
  damping: 25,
  stiffness: 400,
  mass: 0.15,
};

const TILT_SPRING = {
  damping: 30,
  stiffness: 200,
};

const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, [role="button"], .clickable';

export function DroneCursor() {
  const [isDesktop, setIsDesktop] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const smoothX = useSpring(mouseX, CURSOR_SPRING);
  const smoothY = useSpring(mouseY, CURSOR_SPRING);

  const velocityX = useVelocity(smoothX);
  const velocityY = useVelocity(smoothY);

  const rotateX = useTransform(velocityY, [-1000, 1000], [25, -25]);
  const rotateY = useTransform(velocityX, [-1000, 1000], [-25, 25]);

  const tiltX = useSpring(rotateX, TILT_SPRING);
  const tiltY = useSpring(rotateY, TILT_SPRING);

  useEffect(() => {
    const checkDevice = () => setIsDesktop(window.matchMedia('(min-width: 1024px)').matches);
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  useEffect(() => {
    if (!isDesktop) return;

    document.body.style.cursor = 'none';

    const onMouseMove = (e: MouseEvent) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
      if (!isVisible) setIsVisible(true);
    };

    const onMouseDown = () => setIsClicked(true);
    const onMouseUp = () => setIsClicked(false);

    const onMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const interactive = !!target?.closest(INTERACTIVE_SELECTOR);
      setIsHovering(interactive);

      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        setIsVisible(false);
      } else {
        if (!isVisible) setIsVisible(true);
      }
    };

    const onMouseLeave = () => setIsVisible(false);
    const onMouseEnter = () => setIsVisible(true);

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mouseover', onMouseOver);
    document.addEventListener('mouseleave', onMouseLeave);
    document.addEventListener('mouseenter', onMouseEnter);

    return () => {
      document.body.style.cursor = 'auto';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mouseover', onMouseOver);
      document.removeEventListener('mouseleave', onMouseLeave);
      document.removeEventListener('mouseenter', onMouseEnter);
    };
  }, [isDesktop, isVisible, mouseX, mouseY]);

  if (!isDesktop) return null;

  // Desenho da hélice (reutilizável)
  // Formato de "8" alongado e curvo para parecer uma lâmina real
  const PropellerBlade = () => (
    <path
      d="M0 0 C2 -3 5 -6 1 -9 C-3 -6 -2 -2 0 0 M0 0 C-2 3 -5 6 -1 9 C3 6 2 2 0 0"
      fill="currentColor"
      opacity="0.8"
    />
  );

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          style={{
            x: smoothX,
            y: smoothY,
            translateX: '-50%',
            translateY: '-50%',
            rotateX: isHovering ? 0 : tiltX,
            rotateY: isHovering ? 0 : tiltY,
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 9999,
            pointerEvents: 'none',
            perspective: 600,
          }}
          className="pointer-events-none"
        >
          <motion.div
            animate={{
              scale: isClicked ? 0.85 : isHovering ? 1.3 : 1,
            }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className="relative flex items-center justify-center w-10 h-10"
          >
            <motion.div
              animate={{
                rotate: isHovering ? 45 : 0,
              }}
              transition={{ duration: 0.3, ease: 'backOut' }}
              className="relative w-full h-full"
            >
              <svg
                viewBox="0 0 64 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={`w-full h-full drop-shadow-md transition-colors duration-300 ${
                  isHovering ? 'text-primary' : 'text-secondary dark:text-slate-100'
                }`}
              >
                <title>Cursor em formato de drone</title>
                {/* --- MOTORES & HÉLICES --- */}

                {/* Motor Top-Left */}
                <motion.g
                  initial={{ x: 14, y: 14 }} // Posição fixa no SVG
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 0.5, ease: 'linear' }}
                >
                  <PropellerBlade />
                  <circle cx="0" cy="0" r="1.5" fill="currentColor" />
                </motion.g>

                {/* Motor Top-Right (Gira ao contrário para física realista) */}
                <motion.g
                  initial={{ x: 50, y: 14 }}
                  animate={{ rotate: -360 }}
                  transition={{ repeat: Infinity, duration: 0.5, ease: 'linear' }}
                >
                  <PropellerBlade />
                  <circle cx="0" cy="0" r="1.5" fill="currentColor" />
                </motion.g>

                {/* Motor Bottom-Left (Gira ao contrário) */}
                <motion.g
                  initial={{ x: 14, y: 50 }}
                  animate={{ rotate: -360 }}
                  transition={{ repeat: Infinity, duration: 0.5, ease: 'linear' }}
                >
                  <PropellerBlade />
                  <circle cx="0" cy="0" r="1.5" fill="currentColor" />
                </motion.g>

                {/* Motor Bottom-Right */}
                <motion.g
                  initial={{ x: 50, y: 50 }}
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 0.5, ease: 'linear' }}
                >
                  <PropellerBlade />
                  <circle cx="0" cy="0" r="1.5" fill="currentColor" />
                </motion.g>

                {/* --- CORPO DO DRONE --- */}

                {/* Fuselagem central */}
                <path
                  d="M32 22C26 22 22 26 22 32C22 38 26 42 32 42C38 42 42 38 42 32C42 26 38 22 32 22Z"
                  fill="currentColor"
                  fillOpacity="0.15"
                  stroke="currentColor"
                  strokeWidth="2"
                />

                {/* Braços de conexão (X Frame) */}
                <path
                  d="M14 14L23 23"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <path
                  d="M50 14L41 23"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <path
                  d="M14 50L23 41"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <path
                  d="M50 50L41 41"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />

                {/* Câmera/Sensor central (Quadrado arredondado) */}
                <rect x="29" y="29" width="6" height="6" rx="1.5" fill="currentColor" />

                {/* --- HUD DE MIRA (Aparece no Hover) --- */}
                {isHovering && (
                  <motion.g
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                  >
                    {/* Crosshair interna */}
                    <path
                      d="M32 18V14 M32 50V46 M18 32H14 M50 32H46"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    {/* Cantoneiras */}
                    <path
                      d="M22 26L26 22 M42 26L38 22 M22 38L26 42 M42 38L38 42"
                      stroke="currentColor"
                      strokeWidth="1"
                      opacity="0.6"
                    />
                  </motion.g>
                )}
              </svg>
            </motion.div>

            {/* Anel de Travamento de Mira Externo */}
            {isHovering && (
              <motion.div
                layoutId="drone-target"
                className="absolute inset-0 border-2 border-primary rounded-full"
                initial={{ opacity: 0, scale: 1.4 }}
                animate={{ opacity: 1, scale: 1.6, rotate: 90 }}
                exit={{ opacity: 0, scale: 1.4 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                style={{ borderStyle: 'dotted' }}
              />
            )}

            {/* LED de Status (Vermelho - Navegação) */}
            {!isHovering && (
              <motion.div
                className="absolute w-1 h-1 bg-green-500 rounded-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 blur-[1px] z-10"
                animate={{ opacity: [0, 1, 0] }}
                transition={{ duration: 1, repeat: Infinity, times: [0, 0.2, 1] }} // Piscar rápido estilo strobo
              />
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
